import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useAuth } from '@/contexts/AuthContext';
// Mesmo arraste de largura por coluna do Kanban de Solicitações, com chave própria.
import { useSolicitacoesKanbanLargura } from '../../SolicitacoesListPage/components/useSolicitacoesKanbanLargura';
import { LARGURA_COLUNA_PADRAO } from '../../SolicitacoesListPage/components/solicitacoesKanban.utils';
import type { EstadiaCliente, StatusEstadiaCliente } from '../../../models';
import { STATUS_ESTADIA_CLIENTE_LABELS, STATUS_ESTADIA_CLIENTE_OPCOES } from '../equipamentosListPage.definitions';
import type { ContextoBuscaEstadia } from '../estadiasCliente.utils';
import EstadiaKanbanCard from './EstadiaKanbanCard';

export type AgrupamentoKanbanEstadias = 'status' | 'cliente';

interface EstadiasKanbanProps {
  estadias: EstadiaCliente[];
  /** Por Status: só as colunas dos status filtrados (vazio = todas). */
  statusSelecionados: StatusEstadiaCliente[];
  contexto: ContextoBuscaEstadia;
  agrupamento: AgrupamentoKanbanEstadias;
  onChangeAgrupamento: (agrupamento: AgrupamentoKanbanEstadias) => void;
}

interface Coluna {
  chave: string;
  label: string;
  itens: EstadiaCliente[];
}

const OPCOES_AGRUPAMENTO: Array<{ valor: AgrupamentoKanbanEstadias; label: string }> = [
  { valor: 'status', label: 'Por Status' },
  { valor: 'cliente', label: 'Por Cliente' },
];

/**
 * Kanban da aba "Estadia em Cliente" — só visualização (sem arrastar, decisão do usuário:
 * as transições têm modal próprio — confirmar chegada, recolher — e continuam na Lista).
 * Por Status: colunas fixas na ordem do fluxo. Por Cliente: uma coluna por Cliente que
 * tem máquina no recorte atual, em ordem alfabética.
 */
export default function EstadiasKanban({
  estadias,
  statusSelecionados,
  contexto,
  agrupamento,
  onChangeAgrupamento,
}: EstadiasKanbanProps) {
  const { user } = useAuth();
  const { largurasColuna, iniciarArrasteLargura } = useSolicitacoesKanbanLargura(
    user?.uid,
    'si:estadiasKanbanLargurasColuna'
  );
  // Prefixa pelo agrupamento — "Por Status" e "Por Cliente" guardam larguras separadas.
  const chaveLargura = (coluna: Coluna) => `${agrupamento}:${coluna.chave}`;

  const colunas = useMemo<Coluna[]>(() => {
    if (agrupamento === 'status') {
      const status = statusSelecionados.length > 0
        ? STATUS_ESTADIA_CLIENTE_OPCOES.filter((s) => statusSelecionados.includes(s))
        : STATUS_ESTADIA_CLIENTE_OPCOES;
      return status.map((s) => ({
        chave: s,
        label: STATUS_ESTADIA_CLIENTE_LABELS[s],
        itens: estadias.filter((estadia) => estadia.status === s),
      }));
    }
    const porCliente = new Map<string, EstadiaCliente[]>();
    for (const estadia of estadias) {
      porCliente.set(estadia.clienteId, [...(porCliente.get(estadia.clienteId) ?? []), estadia]);
    }
    return Array.from(porCliente.entries())
      .map(([clienteId, itens]) => ({
        chave: clienteId,
        label: contexto.nomeClientePorId.get(clienteId) ?? 'Cliente não encontrado',
        itens,
      }))
      .sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
  }, [agrupamento, statusSelecionados, estadias, contexto.nomeClientePorId]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 rounded-md border border-gray-200 p-0.5 text-xs font-medium w-fit">
        {OPCOES_AGRUPAMENTO.map(({ valor, label }) => (
          <button
            key={valor}
            type="button"
            onClick={() => onChangeAgrupamento(valor)}
            className={clsx(
              'rounded px-2 py-1',
              agrupamento === valor ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-100'
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Largura fixa por coluna (arrastável, persistida) — o board rola na horizontal
          quando passa da tela, em vez de espremer as colunas. */}
      <div className="flex gap-4 overflow-x-auto pb-2">
        {colunas.map((coluna) => (
          <div
            key={coluna.chave}
            className="relative flex shrink-0 flex-col rounded-md bg-gray-50"
            style={{ width: `${largurasColuna[chaveLargura(coluna)] ?? LARGURA_COLUNA_PADRAO}px` }}
          >
            <div className="sticky top-0 rounded-t-md bg-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
              {coluna.label} ({coluna.itens.length})
            </div>
            {/* Mesma grade do Kanban de Solicitações: quantos cards cabem por linha é
                consequência da largura (auto-fill), não um número fixo. */}
            <div
              className="grid content-start gap-2 overflow-y-auto p-2"
              style={{ maxHeight: 'calc(100vh - 320px)', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))' }}
            >
              {coluna.itens.length === 0 && (
                <p className="p-2 text-xs text-gray-400" style={{ gridColumn: '1 / -1' }}>
                  Nenhuma máquina.
                </p>
              )}
              {coluna.itens.map((estadia) => {
                const equipamento = contexto.equipamentoPorId.get(estadia.equipamentoId);
                return (
                  <EstadiaKanbanCard
                    key={estadia.id}
                    estadia={estadia}
                    patrimonio={equipamento?.patrimonio ?? null}
                    modelo={equipamento?.modelo ?? null}
                    nomeCliente={contexto.nomeClientePorId.get(estadia.clienteId) ?? null}
                    nomeColaboradorCotado={
                      estadia.colaboradorCotadoId
                        ? (contexto.nomeColaboradorPorId.get(estadia.colaboradorCotadoId) ?? null)
                        : null
                    }
                    mostrarStatus={agrupamento === 'cliente'}
                    mostrarCliente={agrupamento === 'status'}
                  />
                );
              })}
            </div>
            <div
              onMouseDown={(event) => iniciarArrasteLargura(chaveLargura(coluna), event)}
              className="group absolute right-0 top-0 flex h-full w-2 cursor-col-resize items-center justify-center rounded-r-md"
              title="Arraste pra ajustar a largura da coluna"
            >
              <div className="h-8 w-0.5 rounded-full bg-gray-300 group-hover:bg-blue-400" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
